import { motion } from "framer-motion";
import { useState } from "react";
import { Plus, Search, Check, ChevronDown } from "lucide-react";
import { useStore } from "@/context/StoreContext";

function ColorPills({ value, onChange, testbase }) {
  return (
    <div className="color-pills">
      {["Pink", "Black"].map((c) => (
        <button
          key={c}
          type="button"
          className={`color-pill ${value === c ? "on" : ""}`}
          data-testid={`${testbase}-${c.toLowerCase()}`}
          onClick={() => onChange(c)}
        >
          <span className={`dot ${c.toLowerCase()}`} /> {c}
        </button>
      ))}
    </div>
  );
}

export default function Products() {
  const { t, products, addToCart, money } = useStore();
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("all");
  const [variant, setVariant] = useState({});
  const [setOpts, setSetOpts] = useState({});
  const [openDetails, setOpenDetails] = useState({});

  const tArr = (k) => t(k).split("|").filter(Boolean);

  // v11: All = main products only; Add-ons = pin + straps (never mixed with main products)
  const MAIN_GROUPS = ["knee", "bag", "set"];
  const inCat = (p) => (cat === "all" ? MAIN_GROUPS.includes(p.group) : !MAIN_GROUPS.includes(p.group));

  const q = query.trim().toLowerCase();
  const visible = products.filter(
    (p) => inCat(p) && (!q || `${t(`name.${p.group}`)} ${p.name} ${p.color}`.toLowerCase().includes(q))
  );

  const toggleDetails = (id) => setOpenDetails((s) => ({ ...s, [id]: !s[id] }));

  return (
    <section className="section">
      <div className="container products-shell" id="products" data-testid="products-section">
        <div className="products-layout">
          <div className="products-intro">
            <p className="section-title-small">{t("products.eyebrow")}</p>
            <h2 className="large-title">
              {t("products.title1")}
              <br />
              {t("products.title2")}
            </h2>
            <p className="muted">{t("products.desc")}</p>
            <div className="product-search" data-testid="product-search">
              <Search size={15} />
              <input
                data-testid="product-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("search.placeholder")}
                aria-label={t("search.placeholder")}
              />
            </div>
          </div>

          <div className="shop-right">
            <div className="shop-filterbar" data-testid="shop-filterbar">
              {[["all", "shop.catAll"], ["addons", "shop.catAddons"]].map(([key, label]) => (
                <button
                  key={key}
                  className={`shop-cat-pill ${cat === key ? "on" : ""}`}
                  data-testid={`shop-cat-${key}`}
                  onClick={() => setCat(key)}
                >
                  {t(label)}
                </button>
              ))}
            </div>
            <div className="product-grid" id="product-grid" key={cat}>
            {products.length === 0 && (
              <>
                {[1, 2, 3, 4].map((i) => (
                  <div className="product-skeleton" key={i} data-testid="product-skeleton" />
                ))}
                <div className="products-empty">{t("products.loading")}</div>
              </>
            )}

            {products.length > 0 && visible.length === 0 && (
              <div className="products-empty" data-testid="search-empty">{t("search.none")}</div>
            )}

            {visible.map((p, pi) => {
              const siblings = products.filter((x) => x.name === p.name);
              const selectedId = variant[p.id] ?? p.id;
              const selected = siblings.find((x) => x.id === selectedId) ?? p;
              const isSet = p.group === "set";
              const opts = setOpts[p.id] || { knee: "Pink", bag: "Pink" };
              const stock =
                typeof selected.stock === "number"
                  ? selected.stock
                  : typeof p.stock === "number"
                    ? p.stock
                    : null;
              const soldOut = stock !== null && stock <= 0;
              const lowAt = typeof selected.low_stock_threshold === "number" ? selected.low_stock_threshold : (p.low_stock_threshold ?? 5);
              const hasBenefits = p.group === "knee" || p.group === "bag";

              return (
                <motion.article
                  className="product-card"
                  key={p.id}
                  data-testid={`product-card-${p.id}`}
                  initial={{ opacity: 0, y: 36 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: pi * 0.07 }}
                >
                  <div className={`product-image-wrap ${isSet ? "set" : selected.color.toLowerCase()}`}>
                    {isSet && <span className="set-badge" data-testid="set-badge">{t("set.badge")}</span>}
                    <img
                      className={`product-image ${isSet ? "set-art" : ""}`}
                      src={selected.image}
                      alt={`${selected.color} ${t(`name.${p.group}`)}`}
                      loading="lazy"
                    />
                  </div>
                  <div className="product-info">
                    <div className="product-name">{t(`name.${p.group}`)}</div>
                    {isSet ? (
                      <div className="product-note" data-testid="product-note">{t("set.includes")}</div>
                    ) : (
                      <div className="product-meta">{selected.color}</div>
                    )}

                    <div className="option-block">
                      {isSet ? (
                        <>
                          <div className="set-opt">
                            <small>{t("opt.knee")}</small>
                            <ColorPills
                              value={opts.knee}
                              onChange={(c) => setSetOpts((s) => ({ ...s, [p.id]: { ...opts, knee: c } }))}
                              testbase={`set-knee-${p.id}`}
                            />
                          </div>
                          <div className="set-opt">
                            <small>{t("opt.bag")}</small>
                            <ColorPills
                              value={opts.bag}
                              onChange={(c) => setSetOpts((s) => ({ ...s, [p.id]: { ...opts, bag: c } }))}
                              testbase={`set-bag-${p.id}`}
                            />
                          </div>
                        </>
                      ) : (
                        siblings.length > 1 && (
                          <div className="set-opt">
                            <ColorPills
                              value={selected.color}
                              onChange={(c) => setVariant((v) => ({ ...v, [p.id]: (siblings.find((x) => x.color === c) ?? p).id }))}
                              testbase={`card-color-${p.id}`}
                            />
                          </div>
                        )
                      )}
                    </div>

                    <div className="product-bottom">
                      <div className="price-wrap">
                        <div className="price">{money(selected.price)}</div>
                        {stock !== null && (
                          <span className={`stock-pill ${stock <= 0 ? "out" : stock <= lowAt ? "low" : ""}`} data-testid={`stock-${p.id}`}>
                            {stock <= 0
                              ? t("product.soldOut")
                              : stock <= lowAt
                                ? t("product.onlyLeft", { n: stock })
                                : t("product.inStock")}
                          </span>
                        )}
                      </div>
                      <button
                        className="add-btn"
                        data-testid={`product-add-to-cart-${p.id}`}
                        aria-label={t("product.add")}
                        disabled={soldOut}
                        onClick={() => addToCart(selected, isSet ? opts : null)}
                      >
                        <Plus size={18} />
                      </button>
                    </div>

                    {hasBenefits && !isSet && (
                      <div className="benefits-block" data-testid={`benefits-${p.id}`}>
                        <p className="benefits-title">{t("product.whyTitle")}</p>
                        <ul className="benefits-list">
                          {tArr(`benefits.${p.group}`).slice(0, 4).map((b) => (
                            <li key={b}><Check size={11} /> <span>{b}</span></li>
                          ))}
                        </ul>
                        <button
                          className={`details-toggle ${openDetails[p.id] ? "open" : ""}`}
                          data-testid={`details-toggle-${p.id}`}
                          aria-expanded={!!openDetails[p.id]}
                          onClick={() => toggleDetails(p.id)}
                        >
                          {openDetails[p.id] ? t("product.hideDetails") : t("product.viewDetails")}
                          <ChevronDown size={14} />
                        </button>
                        <div className={`details-wrap ${openDetails[p.id] ? "open" : ""}`}>
                          <div>
                            <div className="details-panel" data-testid={`details-panel-${p.id}`}>
                              <p className="details-h">{t("product.about")}</p>
                              {tArr(`details.${p.group}.about`).map((par, i) => (
                                <p className="details-p" key={i}>{par}</p>
                              ))}
                              <p className="details-h">{t("product.features")}</p>
                              <ul className="details-list">
                                {tArr(`details.${p.group}.features`).map((f) => (
                                  <li key={f}><Check size={11} /> <span>{f}</span></li>
                                ))}
                              </ul>
                              <p className="details-h">{t("product.use")}</p>
                              <p className="details-p">{t(`details.${p.group}.use`)}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* v11: add-ons (pin/strap) get the same details panel, without the benefits list */}
                    {!hasBenefits && !isSet && (
                      <div className="benefits-block" data-testid={`benefits-${p.id}`}>
                        <button
                          className={`details-toggle ${openDetails[p.id] ? "open" : ""}`}
                          data-testid={`details-toggle-${p.id}`}
                          aria-expanded={!!openDetails[p.id]}
                          onClick={() => toggleDetails(p.id)}
                        >
                          {openDetails[p.id] ? t("product.hideDetails") : t("product.viewDetails")}
                          <ChevronDown size={14} />
                        </button>
                        <div className={`details-wrap ${openDetails[p.id] ? "open" : ""}`}>
                          <div>
                            <div className="details-panel" data-testid={`details-panel-${p.id}`}>
                              <p className="details-h">{t("product.about")}</p>
                              {tArr(`details.${p.group}.about`).map((par, i) => (
                                <p className="details-p" key={i}>{par}</p>
                              ))}
                              <p className="details-h">{t("product.features")}</p>
                              <ul className="details-list">
                                {tArr(`details.${p.group}.features`).map((f) => (
                                  <li key={f}><Check size={11} /> <span>{f}</span></li>
                                ))}
                              </ul>
                              <p className="details-h">{t("product.use")}</p>
                              <p className="details-p">{t(`details.${p.group}.use`)}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.article>
              );
            })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
